// =============================================================================
// Contract Types for Contracts Page
// Data source: MongoDB via GET /v3/contracts API (V3)
// =============================================================================

/**
 * Payment terms options
 */
export type PaymentTerms = 'on-order' | 'net-0' | 'net-30' | 'net-45' | 'net-60';

/**
 * Currency options
 */
export type Currency = 'CAD' | 'USD' | 'EUR';

/**
 * Language options
 */
export type Language = 'en' | 'fr';

/**
 * Populated organisation reference from V3
 */
export interface PopulatedOrganisation {
  _id: string;
  name: string;
  org_num?: number;
  enabled?: boolean;
}

/**
 * Populated user reference from V3
 */
export interface PopulatedUser {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
}

/**
 * Populated site reference from V3
 */
export interface PopulatedSite {
  _id: string;
  name: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}

/**
 * V3 Contract from MongoDB
 */
export interface Contract {
  _id: string;
  contract_number: string;
  name: string;

  // Customer & references
  customer?: string | PopulatedOrganisation;

  // Managers
  sales_manager?: string | PopulatedUser;
  contractual_managers?: Array<string | PopulatedUser>;
  technical_managers?: Array<string | PopulatedUser>;

  // Sites
  billing_sites?: Array<string | PopulatedSite>;
  shipping_sites?: Array<string | PopulatedSite>;

  // Config
  currency?: Currency;
  language?: Language;
  payment_terms?: PaymentTerms;

  // Dates
  start_date?: string;
  end_date?: string;
  created_at?: string;
  updated_at?: string;

  // Links
  gdrive_link?: string;

  // Clause
  contractual_clause?: string;

  // System
  deleted?: boolean;
}

/**
 * Filters for querying contracts
 */
export interface ContractsFilters {
  search?: string;
  customer?: string;
  sales_manager?: string;
  sortKey?: 'contract_number' | 'name' | 'created_at' | 'start_date' | 'end_date';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v3/contracts
 */
export interface ContractsResponse {
  contracts: Contract[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Input for creating a new contract
 */
export interface CreateContractInput {
  customer: string;
  name: string;
  sales_manager: string;
  currency?: Currency;
  language?: Language;
  payment_terms?: PaymentTerms;
  start_date?: string;
  end_date?: string;
  gdrive_link?: string;
}

/**
 * Input for updating a contract
 */
export interface UpdateContractInput {
  name?: string;
  customer?: string;
  sales_manager?: string;
  contractual_managers?: string[];
  technical_managers?: string[];
  billing_sites?: string[];
  shipping_sites?: string[];
  currency?: Currency;
  language?: Language;
  payment_terms?: PaymentTerms;
  start_date?: string;
  end_date?: string;
  gdrive_link?: string;
  contractual_clause?: string;
}

/**
 * Organisation for dropdown filter (customer)
 */
export interface Organisation {
  _id: string;
  name: string;
  org_num?: number;
  enabled?: boolean;
}

/**
 * User for dropdown filter (managers)
 */
export interface User {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isPopulatedOrganisation(
  org: string | PopulatedOrganisation | undefined
): org is PopulatedOrganisation {
  return typeof org === 'object' && org !== null && '_id' in org;
}

export function isPopulatedUser(
  user: string | PopulatedUser | undefined
): user is PopulatedUser {
  return typeof user === 'object' && user !== null && '_id' in user;
}

export function isPopulatedSite(
  site: string | PopulatedSite | undefined
): site is PopulatedSite {
  return typeof site === 'object' && site !== null && '_id' in site;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the display name for a customer
 */
export function getCustomerName(customer: string | PopulatedOrganisation | undefined): string {
  if (!customer) return '-';
  if (isPopulatedOrganisation(customer)) {
    return customer.name || '-';
  }
  return customer;
}

/**
 * Get the display name for a user (sales manager, etc.)
 */
export function getUserName(user: string | PopulatedUser | undefined): string {
  if (!user) return '-';
  if (isPopulatedUser(user)) {
    const parts = [];
    if (user.firstname) parts.push(user.firstname);
    if (user.lastname) parts.push(user.lastname);
    return parts.join(' ') || user.email || '-';
  }
  return user;
}

/**
 * Check if subscription end date has passed
 */
export function isSubscriptionExpired(subscription: Contract): boolean {
  if (!subscription.end_date) return false;
  return new Date(subscription.end_date) < new Date();
}

/** @deprecated Use isSubscriptionExpired */
export const isContractExpired = isSubscriptionExpired;

/**
 * Check if subscription end date is approaching (within 7 days)
 */
export function isSubscriptionExpiring(subscription: Contract): boolean {
  if (!subscription.end_date) return false;
  const endDate = new Date(subscription.end_date);
  const now = new Date();
  const daysUntilEnd = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntilEnd <= 7 && daysUntilEnd > 0;
}

/** @deprecated Use isSubscriptionExpiring */
export const isContractExpiring = isSubscriptionExpiring;

// =============================================================================
// V4 Types (PostgreSQL)
// =============================================================================

export type SubscriptionStatus = 'draft' | 'active' | 'inactive' | 'expired' | 'cancelled' | 'pending_renewal';

/** @deprecated Use SubscriptionStatus */
export type ContractStatus = SubscriptionStatus;

export const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  'draft', 'active', 'inactive', 'expired', 'cancelled', 'pending_renewal',
];

/** @deprecated Use SUBSCRIPTION_STATUSES */
export const CONTRACT_STATUSES = SUBSCRIPTION_STATUSES;

export interface V4Subscription {
  id: string;
  subscriptionNumber: string;
  name: string;
  description?: string | null;
  contractualClause?: string | null;
  status: SubscriptionStatus;
  startDate: string | null;
  endDate: string | null;
  signedDate?: string | null;
  /**
   * The AM's manual override of the renewal billing date (#1397 D1). Normally null:
   * the window is derived from the anniversary of `startDate`, and a non-null value
   * here is what makes the engine stop deriving.
   */
  billingDate?: string | null;
  /**
   * TRUE prepaid marker (#1402 rider B): the contract was paid up front as a block,
   * so no renewal offer is made before `endDate`. Most multi-year contracts billed
   * annually (Boiron, CGC, DND) stay false — their end date alone never blocks.
   */
  prepaid?: boolean;
  autoRenew: boolean;
  renewalNoticeDays: number;
  // #1409 — annual value HT/an; total re-derives (annual × years) on edit.
  annualValue: string | null;
  totalValue: string | null;
  currency: string;
  paymentTerms: string | null;
  salesManagerId: string | null;
  contractualManagerIds: string[];
  technicalManagerIds: string[];
  gdriveFolderId: string | null;
  hubspotDealId?: string | null;
  mongoContractId?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  // Joined fields (from getMany)
  accountId: string;
  accountName: string | null;
  accountNumber: string | null;
  salesManagerName: string | null;
  contractualManagerName: string | null;
  defaultBillingSiteId?: string | null;
  invoiceCount?: number;
  // Per-subscription quantity columns (Hugo #1084 p7). ssrQty/calQty are junction-based
  // (need the subscription_sensors backfill); lteQty is the account's activated SIM count.
  ssrQty?: number;
  calQty?: number;
  lteQty?: number;
  // Resolved managers (from getOneByID)
  contractualManagers?: Array<{ id: string; name: string; email?: string | null }>;
  technicalManagers?: Array<{ id: string; name: string; email?: string | null }>;
}

/** @deprecated Use V4Subscription */
export type V4Contract = V4Subscription;

export interface V4SubscriptionsResponse {
  subscriptions: V4Subscription[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/** @deprecated Use V4SubscriptionsResponse */
export type V4ContractsResponse = V4SubscriptionsResponse;

export interface V4SubscriptionDetailResponse {
  subscription: V4Subscription;
}

export interface SubscriptionManagerCandidate {
  id: string;
  name: string;
  email: string | null;
  enabled: boolean;
}

/** @deprecated Use V4SubscriptionDetailResponse */
export type V4ContractDetailResponse = V4SubscriptionDetailResponse;

export interface V4InvoiceSku {
  id: string;
  sku: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  discount: string;
  total: string;
}

export interface V4SubscriptionInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  invoiceDate: string | null;
  dueDate: string | null;
  paymentDate: string | null;
  subtotal: string;
  taxAmount: string;
  taxCategory: string | null;
  total: string;
  currency: string;
  projectName: string | null;
  poNumber: string | null;
  paymentTerms: string | null;
  contractualManagerId: string | null;
  salesManagerId: string | null;
  contractualManagerName: string | null;
  contractualManagerEmail: string | null;
  salesManagerName: string | null;
  quoteNumber: string | null;
  dealType: string | null;
  skus: V4InvoiceSku[];
  skuCount: number;
}

/** @deprecated Use V4SubscriptionInvoice */
export type V4ContractInvoice = V4SubscriptionInvoice;

export interface V4SubscriptionInvoicesResponse {
  invoices: V4SubscriptionInvoice[];
}

/** @deprecated Use V4SubscriptionInvoicesResponse */
export type V4ContractInvoicesResponse = V4SubscriptionInvoicesResponse;

export interface V4QuoteLineItem {
  id: string;
  sku: string | null;
  name: string | null;
  description: string | null;
  quantity: string;
  unitPrice: string;
  discount: string;
  total: string;
}

export interface V4SubscriptionQuote {
  id: string;
  quoteNumber: string;
  name: string | null;
  status: string;
  createdDate: string;
  expirationDate: string | null;
  subtotal: string;
  discount: string;
  taxAmount: string;
  total: string;
  currency: string;
  projectName: string | null;
  poNumber: string | null;
  lineItems: V4QuoteLineItem[];
  lineItemCount: number;
}

export interface V4SubscriptionQuotesResponse {
  quotes: V4SubscriptionQuote[];
}

export interface AssignInvoicesResponse {
  assigned: number;
  linkedQuotes: string[];
}

export interface UnassignInvoiceResponse {
  success: boolean;
  unlinkedQuotes: string[];
}

export interface AssignQuotesResponse {
  assigned: number;
}

export interface UnassignQuoteResponse {
  success: boolean;
}

export interface CreateSubscriptionV4Input {
  accountId: string;
  name: string;
  salesManagerId?: string;
  status?: SubscriptionStatus;
  currency?: string;
  paymentTerms?: string;
  startDate?: string;
  endDate?: string;
  gdriveFolderId?: string;
  description?: string;
  contractualClause?: string;
  contractualManagerIds?: string[];
  technicalManagerIds?: string[];
}

/** @deprecated Use CreateSubscriptionV4Input */
export type CreateContractV4Input = CreateSubscriptionV4Input;

export interface UpdateSubscriptionV4Input {
  name?: string;
  description?: string;
  contractualClause?: string;
  status?: SubscriptionStatus;
  startDate?: string | null;
  endDate?: string | null;
  signedDate?: string | null;
  /**
   * Manual OVERRIDE of the renewal billing date (#1397 D1). Leave it null/'' and the
   * window is derived from the start_date anniversary, which is the normal case; set it
   * only to correct a subscription whose real cycle does not follow its start date.
   */
  billingDate?: string | null;
  /** #1402 rider B — set true only for contracts paid up front through their end date. */
  prepaid?: boolean;
  autoRenew?: boolean;
  renewalNoticeDays?: number;
  annualValue?: string | null;
  totalValue?: string | null;
  currency?: string;
  paymentTerms?: string | null;
  salesManagerId?: string | null;
  contractualManagerIds?: string[];
  technicalManagerIds?: string[];
  gdriveFolderId?: string | null;
  defaultBillingSiteId?: string | null;
  accountId?: string;
}

/** @deprecated Use UpdateSubscriptionV4Input */
export type UpdateContractV4Input = UpdateSubscriptionV4Input;

export interface V4SubscriptionsFilters {
  search?: string;
  accountId?: string;
  status?: string;
  salesManagerId?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  sortKey?: string;
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/** @deprecated Use V4SubscriptionsFilters */
export type V4ContractsFilters = V4SubscriptionsFilters;

// Duration helper
export function computeDuration(startDate: string | null, endDate: string | null): string | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const totalMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (totalMonths < 1) {
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `${days}d`;
  }
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years > 0 && months > 0) return `${years}y ${months}m`;
  if (years > 0) return `${years}y`;
  return `${months}m`;
}

// Status color mapping
export function getStatusVariant(status: SubscriptionStatus): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'active': return 'success';
    case 'draft': return 'secondary';
    case 'inactive': return 'default';
    case 'expired': return 'destructive';
    case 'cancelled': return 'outline';
    case 'pending_renewal': return 'warning';
    default: return 'default';
  }
}

// =============================================================================
// Subscription Sensor Types
// =============================================================================

export interface SubscriptionSensor {
  id: string;
  mongoSensorId: string;
  name: string;
  status: number | null;
  customId: string | null;
  streamName: string | null;
  invoices: string[];
  quotes: string[];
  subscriptions: string[];
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
}

// =============================================================================
// Renewal Summary (read-only) — GET /v4/subscriptions/:id/renewal-summary
// The DAO returns stable warning CODES; the client translates them (en/fr).
// =============================================================================

export interface RenewalWarning {
  code: string;
  severity: 'high' | 'medium' | 'info';
  detail?: Record<string, unknown>;
}

export interface RenewalReferenceInvoice {
  id: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  status: string | null;
  total: string | number | null;
  currency: string | null;
  quoteNumber: string | null;
  /** #1406 — true when the reference is the purchase-invoice fallback (no
   *  recurring/pure-service invoice exists; SA/CS/LTE lines only). */
  isPurchaseFallback?: boolean;
}

export interface RenewalSummary {
  subscription: {
    id: string;
    subscriptionNumber?: string | null;
    name?: string | null;
    status?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    renewalNoticeDays?: number | null;
  };
  channels: { linked: number; account: number; unlinked: number };
  sims: { count: number; scope: string; usageUnknown: boolean };
  referenceInvoice: RenewalReferenceInvoice | null;
  billed: { sa: number; cs: number; lte: number };
  deltas: { channels: number; sims: number };
  tier: { crossed: boolean; tier: number | null; sku: string | null };
  lumpSum: boolean;
  calibrationFlag: string;
  lines: unknown[];
  warnings: RenewalWarning[];
}

// =============================================================================
// Renewal reconciliation lists (read-only) — GET /v4/subscriptions/:id/reconciliation
// The two D-04 lists derived from the subscription_sensors junction.
// =============================================================================

export interface ReconciliationSensor {
  id: string;
  mongoSensorId: string | null;
  name: string | null;
  status: number | null;
  customId: string | null;
  streamName: string | null;
  archived: boolean;
  deleted: boolean;
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
}

export interface ReconciliationLists {
  subscriptionId: string;
  /** Linked to this subscription but no longer live — remove candidates. */
  archivedLinked: ReconciliationSensor[];
  /** Live account sensors with no subscription link — add candidates. */
  unlinkedActive: ReconciliationSensor[];
}

// Recurring-revenue portfolio totals (S11) — GET /v4/subscriptions/portfolio-totals.
export interface PortfolioTotals {
  count: number;
  /** Total recurring value (SUM of total_value) grouped by currency. */
  byCurrency: Record<string, number>;
  /** Renewing subscriptions reaching their end date within the next 90 days. */
  expiringSoon: number;
}

/** One subscription inside an "Expected revenue by month" bucket (#1409). */
export interface RevenueByMonthItem {
  subscriptionId: string | null;
  subscriptionNumber: string | null;
  accountName: string | null;
  currency: string;
  amount: number;
  status: 'due' | 'quoted' | 'invoiced' | 'paid';
  ownerName: string | null;
}

/** One month of the expected-revenue view: every month of the window is present, empty ones included. */
export interface RevenueByMonthBucket {
  /** YYYY-MM */
  month: string;
  /** Sum of annual values per currency — currencies are never merged. */
  totals: Record<string, number>;
  items: RevenueByMonthItem[];
}

export interface RevenueByMonthResponse {
  months: RevenueByMonthBucket[];
}

// =============================================================================
// Renewal quote preview + creation (S7/S8) — GET/POST /v4/subscriptions/:id/renewal-quote
// =============================================================================

export interface RenewalQuoteLine {
  sku: string | null;
  description: string | null;
  quantity: number;
  unitPrice: number;
  discount: number;
  sortOrder: number;
  kind?: 'full' | 'prorated';
}

export interface RenewalQuotePreview {
  subscriptionId: string;
  referenceInvoice: RenewalReferenceInvoice | null;
  /** Which source built the lines: the verified subscription lines, or a copy
   *  of the reference invoice (#1396 / #1406 — never a mix of the two). */
  source: 'lines' | 'reference_invoice';
  /**
   * `end` is the LAST COVERED DAY (billing date + 12 months − 1 day) — what the UI and
   * the documents show. `endExclusive` is the boundary the coverage math uses; they
   * describe the same interval (#1397 D1). `anchoredOn` is 'start_date_anniversary'
   * when derived, 'billing_date_override' when an AM set one.
   */
  window: {
    start: string | null;
    end: string | null;
    endExclusive?: string | null;
    anchoredOn: string;
  };
  linkedChannelCount: number;
  sensorsToVerify: number;
  lines: RenewalQuoteLine[];
  warnings: RenewalWarning[];
  total: number;
}

export interface CreatedRenewalQuote {
  quote: { id: string; quoteNumber: string | number; total: string | number; dealType: string; status: string };
  linkedSensors: number;
}
